import {
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Playlist } from '@raydiant/api-client-js';
import Spinner from '@raydiant/raydial/components/Spinner';
import Text from '@raydiant/raydial/components/Text';
import Modal from '@raydiant/raydial/components/Modal';
import Button from '@raydiant/raydial/components/Button';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import { ArrowLeft } from '@raydiant/raydial/components/Icons';
import { isNewId } from '../../../utilities/identifiers';
import Page from '../../../components/Page';
import usePlaylistsById from '../../../hooks/usePlaylistsById';
import usePlaylists from '../../../hooks/usePlaylists';
import usePresentations from '../../../hooks/usePresentations';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useCopyPlaylist from '../../../hooks/useCopyPlaylist';
import { useToastState } from '@raydiant/raydial/components/Toast';
import * as addContentPageActions from '../../AddContentPage/actions';
import {
  selectItemsToAdd,
  selectAddToPlaylistIdBySelectionId,
} from '../../AddContentPage/selectors';
import { getDuplicateResourceName } from '../../../utilities';
import analytics from '../../../clients/analyticsClient';
import { selectLocalUploads } from '../../../selectors/fileUploads';
import { getPresentationFileStatus } from '../../../utilities/presentationUtils';
import usePlaylistPageState from '../usePlaylistPageState';
import PlaylistPageContext from '../PlaylistPageContext';
import PlaylistItemModal from '../PlaylistItemModal';
import { deserializeIDPath } from '../usePlaylistPageState/utilities';
import { ItemIDPath } from '../playlistPageTypes';
import miraClient from '../../../clients/raydiant';
import * as paths from '../../../routes/paths';
import PlaylistHeaderV2 from './components/PlaylistHeaderV2';
import PlaylistNameInput from './components/PlaylistNameInput';
import PlaylistItemListV2 from './components/PlaylistItemListV2';
import PlaylistPreviewV2 from './components/PlaylistPreviewV2';
import DeletePlaylistDialog from './components/DeletePlaylistDialog/DeletePlaylistDialog';
import PlaylistScheduleModalV2 from './components/PlaylistScheduleModalV2/PlaylistScheduleModalV2';
import { shouldHideCompletedAutoRemovePlaylist } from './components/PlaylistItemV2/scheduleVisibility';
import ScheduleVisibilityContext from './components/PlaylistItemV2/ScheduleVisibilityContext';

const getUiStateStorageKey = (sessionId?: string) =>
  `PlaylistPageV2UIState:${sessionId || 'default'}`;

const readUiState = (
  sessionId?: string,
): { uploadingIds: string[]; recentlyAddedIds: string[] } | null => {
  try {
    const raw = sessionStorage.getItem(getUiStateStorageKey(sessionId));
    if (!raw) return null;
    return JSON.parse(raw) as {
      uploadingIds: string[];
      recentlyAddedIds: string[];
    };
  } catch {
    return null;
  }
};

const getInitialSessionId = () =>
  new URLSearchParams(window.location.search).get('sessionId') ?? undefined;

const PlaylistPageV2 = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const toast = useToastState();

  const [uploadingIds, setUploadingIds] = useState<Set<string>>(() => {
    const cached = readUiState(getInitialSessionId());
    return new Set(cached?.uploadingIds || []);
  });
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(() => {
    const cached = readUiState(getInitialSessionId());
    return new Set(cached?.recentlyAddedIds || []);
  });

  const localUploads = useSelector(selectLocalUploads);

  const page = usePlaylistPageState({
    uploadingIds,
    recentlyAddedIds,
  });
  const { rootPlaylistId, isNewPlaylist, queryParams, submitStatus } = page;

  if (!rootPlaylistId) {
    throw new Error('PlaylistPageV2 is missing playlistId.');
  }

  // Selectors
  const itemsToAdd = useSelector(selectItemsToAdd);
  const playlistPageItemsToAdd = itemsToAdd['PlaylistPage'];
  const addToPlaylistIdBySelectionId = useSelector(
    selectAddToPlaylistIdBySelectionId,
  );
  const reduxAddToPlaylistId = addToPlaylistIdBySelectionId['PlaylistPage'];

  // Queries
  const { status: currentUserStatus, data: currentUser } = useCurrentUser();

  const initialPlaylistIds = useRef([
    ...(isNewPlaylist ? [] : [rootPlaylistId]),
    ...Object.keys(page.state.expandedPlaylistIds).filter((id) => !isNewId(id)),
    ...Object.keys(page.state.addedPlaylistIds).filter((id) => !isNewId(id)),
  ]);
  const { status: playlistsStatus, data: playlists } = usePlaylists(
    initialPlaylistIds.current,
    {
      enabled: initialPlaylistIds.current.length > 0,
    },
  );

  const initialPresentationIds = useRef([
    ...Object.keys(page.state.addedPresentationIds).filter(
      (id) => !isNewId(id),
    ),
  ]);
  const { status: presentationsStatus, data: presentations } = usePresentations(
    initialPresentationIds.current,
    {
      enabled: initialPresentationIds.current.length > 0,
    },
  );

  // Page status
  const didPageError =
    playlistsStatus === 'error' ||
    presentationsStatus === 'error' ||
    currentUserStatus === 'error';

  const isPageLoading =
    playlistsStatus === 'loading' ||
    presentationsStatus === 'loading' ||
    currentUserStatus === 'loading';

  let didPageLoad = false;
  if (
    initialPlaylistIds.current.length > 0 &&
    initialPresentationIds.current.length > 0
  ) {
    didPageLoad =
      playlistsStatus === 'success' &&
      presentationsStatus === 'success' &&
      currentUserStatus === 'success';
  } else if (initialPlaylistIds.current.length > 0) {
    didPageLoad =
      playlistsStatus === 'success' && currentUserStatus === 'success';
  } else if (initialPresentationIds.current.length > 0) {
    didPageLoad =
      presentationsStatus === 'success' && currentUserStatus === 'success';
  } else {
    didPageLoad = currentUserStatus === 'success';
  }

  // Callbacks
  const handleBack = useCallback(() => {
    history.push(queryParams.backTo);
  }, [history, queryParams.backTo]);

  // Effects

  const setSavedPlaylists = page.setSavedPlaylists;
  useLayoutEffect(() => {
    if (!playlists) return;
    setSavedPlaylists(playlists);
  }, [playlists, setSavedPlaylists]);

  const setSavedPresentations = page.setSavedPresentations;
  useLayoutEffect(() => {
    if (!presentations) return;
    setSavedPresentations(presentations);
  }, [presentations, setSavedPresentations]);

  const createPlaylist = page.createPlaylist;
  const updatePlaylist = page.updatePlaylist;
  const didCreateNewPlaylist = useRef(false);
  useLayoutEffect(() => {
    if (!isNewPlaylist) return;
    if (!currentUser) return;
    if (didCreateNewPlaylist.current) return;

    const playlistName = queryParams.playlistName;
    createPlaylist(rootPlaylistId, currentUser.id, playlistName);

    if (!playlistName) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      updatePlaylist(rootPlaylistId, {
        name: `New Playlist - ${now.getFullYear()}/${month}/${day} ${hours}:${minutes}`,
      });
    }

    didCreateNewPlaylist.current = true;
  }, [
    rootPlaylistId,
    createPlaylist,
    updatePlaylist,
    currentUser,
    isNewPlaylist,
    queryParams.playlistName,
  ]);

  // Add items that were selected from AddContentPage.
  const addPlaylistItems = page.addPlaylistItems;
  const removePlaylistItemsByIds = page.removePlaylistItemsByIds;
  const setSavedPlaylist = page.setSavedPlaylist;
  const setSavedPresentation = page.setSavedPresentation;
  const isAddingItemsRef = useRef(false);
  useEffect(() => {
    if (!playlistPageItemsToAdd || playlistPageItemsToAdd.length === 0) return;
    if (isAddingItemsRef.current) return;
    if (!didPageLoad) return;
    isAddingItemsRef.current = true;

    const addItemsAfterPath = queryParams.addItemsAfterPath
      ? deserializeIDPath(queryParams.addItemsAfterPath)
      : [];
    const effectiveAddToPlaylistId = reduxAddToPlaylistId;
    const effectiveInsertMode = effectiveAddToPlaylistId ? 'inside' : 'after';
    const destinationPlaylistId =
      effectiveAddToPlaylistId ?? rootPlaylistId ?? '';

    const missingPlaylistIds = playlistPageItemsToAdd
      .filter(
        (item) =>
          item.playlistId && !item.playlist && !isNewId(item.playlistId),
      )
      .map((item) => item?.playlistId || '');

    const missingPresentationIds = playlistPageItemsToAdd
      .filter((item) => item.presentationId && !item.presentation)
      .map((item) => item?.presentationId || '');

    const hasMissingData =
      missingPlaylistIds.length > 0 || missingPresentationIds.length > 0;

    const itemIds = playlistPageItemsToAdd.map((item) => item.id);

    if (hasMissingData) {
      setUploadingIds((prev) => new Set([...prev, ...itemIds]));
    }

    playlistPageItemsToAdd.forEach((item) => {
      if (item.playlistId) {
        initialHiddenPlaylistIdsRef.current[item.playlistId] = false;
      }
    });

    addPlaylistItems(
      effectiveAddToPlaylistId ? addItemsAfterPath : [],
      playlistPageItemsToAdd,
      effectiveInsertMode,
      effectiveAddToPlaylistId,
    );

    const nextPlaylistPageItemsToAdd = [...playlistPageItemsToAdd];

    const fetchData = async () => {
      try {
        if (missingPlaylistIds.length > 0) {
          const fetchedPlaylists = await miraClient.getPlaylists({
            ids: missingPlaylistIds,
          });
          fetchedPlaylists.forEach((playlist) => {
            setSavedPlaylist(playlist);
            const index = nextPlaylistPageItemsToAdd.findIndex(
              (item) => item.playlistId === playlist.id,
            );
            if (index !== -1) {
              nextPlaylistPageItemsToAdd[index] = {
                ...nextPlaylistPageItemsToAdd[index],
                playlist,
              };
            }
          });
        }
        if (missingPresentationIds.length > 0) {
          const fetchedPresentations = await miraClient.getPresentations({
            ids: missingPresentationIds,
          });
          fetchedPresentations.forEach((presentation) => {
            setSavedPresentation(presentation);
            const index = nextPlaylistPageItemsToAdd.findIndex(
              (item) => item.presentationId === presentation.id,
            );
            if (index !== -1) {
              nextPlaylistPageItemsToAdd[index] = {
                ...nextPlaylistPageItemsToAdd[index],
                presentation,
              };
            }
          });
        }

        setUploadingIds((prev) => {
          const next = new Set(prev);
          itemIds.forEach((id) => next.delete(id));
          return next;
        });
        setRecentlyAddedIds((prev) => new Set([...prev, ...itemIds]));

        dispatch(
          addContentPageActions.clearItemsToAdd({
            selectionId: 'PlaylistPage',
          }),
        );
      } catch (error) {
        console.error('error', error);
        removePlaylistItemsByIds(destinationPlaylistId, itemIds);
        setUploadingIds((prev) => {
          const next = new Set(prev);
          itemIds.forEach((id) => next.delete(id));
          return next;
        });
      } finally {
        isAddingItemsRef.current = false;
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    addPlaylistItems,
    removePlaylistItemsByIds,
    setSavedPlaylist,
    setSavedPresentation,
    playlistPageItemsToAdd,
    queryParams.addItemsAfterPath,
    rootPlaylistId,
    didPageLoad,
  ]);

  useEffect(() => {
    if (submitStatus === 'success') {
      setRecentlyAddedIds(new Set());
    }
  }, [submitStatus]);

  useEffect(() => {
    sessionStorage.setItem(
      getUiStateStorageKey(queryParams.sessionId),
      JSON.stringify({
        uploadingIds: [...uploadingIds],
        recentlyAddedIds: [...recentlyAddedIds],
      }),
    );
  }, [queryParams.sessionId, uploadingIds, recentlyAddedIds]);

  const cycleIDPath = page.cycleIDPath;
  const prevCycleIDPathRef = useRef<ItemIDPath | null>(null);
  useEffect(() => {
    if (cycleIDPath && !prevCycleIDPathRef.current) {
      toast.add({
        title: 'Unable to add playlist',
        description: "A playlist can't be nested inside itself",
        variant: 'caution',
      });
    }
    prevCycleIDPathRef.current = cycleIDPath;
  }, [cycleIDPath, toast]);

  // Auto-select the first presentation item in the root playlist for preview.
  const setPreviewItem = page.setPreviewItem;
  const setInitialSelected = page.setInitialSelected;
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (!didPageLoad || didAutoSelectRef.current) return;
    if (!rootPlaylistId) return;
    const rootPlaylist = page.getPlaylist(rootPlaylistId);
    if (!rootPlaylist || rootPlaylist.items.length === 0) return;

    const firstPresentationItem = rootPlaylist.items.find(
      (item) => item.presentationId,
    );
    if (firstPresentationItem) {
      const itemIdPath = [firstPresentationItem.id];
      setInitialSelected(itemIdPath);
      setPreviewItem(firstPresentationItem);
      didAutoSelectRef.current = true;
    }
  }, [didPageLoad, rootPlaylistId, page, setPreviewItem, setInitialSelected]);

  // Render
  const playlistsById = usePlaylistsById(playlists);

  const pageTitle = isNewPlaylist
    ? 'New Playlist'
    : playlistsById[rootPlaylistId]?.name;

  const pagePlaylist = rootPlaylistId
    ? page.getPlaylist(rootPlaylistId)
    : undefined;

  const initialHiddenPlaylistIdsRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    initialHiddenPlaylistIdsRef.current = {};
  }, [rootPlaylistId]);

  useEffect(() => {
    const savedPlaylists = Object.values(page.state.savedPlaylistsById);
    for (const playlist of savedPlaylists) {
      if (initialHiddenPlaylistIdsRef.current[playlist.id] !== undefined) {
        continue;
      }
      initialHiddenPlaylistIdsRef.current[playlist.id] =
        shouldHideCompletedAutoRemovePlaylist(playlist);
    }
  }, [page.state.savedPlaylistsById]);

  const shouldHidePlaylistByInitialLoad = useCallback(
    (playlist?: Playlist | null) => {
      if (!playlist?.id) return false;
      const snapshot = initialHiddenPlaylistIdsRef.current[playlist.id];
      if (snapshot !== undefined) {
        return snapshot;
      }
      const hidden = shouldHideCompletedAutoRemovePlaylist(playlist);
      initialHiddenPlaylistIdsRef.current[playlist.id] = hidden;
      return hidden;
    },
    [],
  );

  const isItemVisibleInCurrentTree = useCallback(
    (targetItemId: string) => {
      const visit = (
        playlist?: typeof pagePlaylist,
        visitedPlaylistIds: Set<string> = new Set(),
      ): boolean => {
        if (!playlist) return false;
        if (visitedPlaylistIds.has(playlist.id)) return false;
        visitedPlaylistIds.add(playlist.id);

        for (const item of playlist.items) {
          if (item.id === targetItemId) return true;

          if (!item.playlistId) continue;
          const nestedPlaylist =
            page.getPlaylist(item.playlistId) ?? item.playlist ?? undefined;
          if (shouldHidePlaylistByInitialLoad(nestedPlaylist)) {
            continue;
          }
          if (visit(nestedPlaylist, visitedPlaylistIds)) return true;
        }

        return false;
      };

      return visit(pagePlaylist);
    },
    [page, pagePlaylist, shouldHidePlaylistByInitialLoad],
  );

  useEffect(() => {
    if (!didPageLoad) return;
    const previewItem = page.state.previewItem;
    if (!previewItem) return;
    if (isItemVisibleInCurrentTree(previewItem.id)) return;

    // Clear stale preview when selected item is removed/hidden from the current tree.
    setPreviewItem(null);
  }, [
    didPageLoad,
    page.state.previewItem,
    isItemVisibleInCurrentTree,
    setPreviewItem,
  ]);

  const selectLibraryItems = useCallback(() => {
    const pageUrl = page.getPageUrl({
      ...queryParams,
    });

    history.push(
      paths.presentations({
        folderId: queryParams.folderId, // Preserve folder context so the picker opens in the same folder
        selectionId: 'PlaylistPage',
        backTo: pageUrl,
        addToLabel: pagePlaylist?.name || 'Playlist',
        saveTo: pageUrl,
        sessionId: queryParams.sessionId,
        scope: queryParams.scope, // Propagate library scope (owned/shared/shared_library) to the add-content modal
      }),
    );
  }, [queryParams, history, page, pagePlaylist]);

  const title = isNewPlaylist ? 'New Playlist' : 'Edit Playlist';

  // Playlist name input state
  const [playlistName, setPlaylistName] = useState(pagePlaylist?.name ?? '');
  const [isNameTouched, setIsNameTouched] = useState(false);

  useEffect(() => {
    if (pagePlaylist?.name !== undefined) {
      setPlaylistName(pagePlaylist.name);
      setIsNameTouched(false);
    }
  }, [pagePlaylist?.name]);

  const handleNameChange = useCallback((nextName: string) => {
    setPlaylistName(nextName);
    setIsNameTouched(true);
  }, []);

  const handleNameBlur = useCallback(() => {
    if (!rootPlaylistId) return;
    setIsNameTouched(true);
    page.updatePlaylist(rootPlaylistId, { name: playlistName });
  }, [page, rootPlaylistId, playlistName]);

  const createItemFromPlaylist = useCallback(
    (playlist: NonNullable<typeof pagePlaylist>) => ({
      id: playlist.id,
      playlistId: playlist.id,
      playlist,
      presentationId: null,
    }),
    [],
  );

  const handleScheduleClick = useCallback(() => {
    if (!pagePlaylist) return;
    analytics.trackPlaylistScheduleOpened({ playlistId: pagePlaylist.id });
    setScheduleModalPlaylist(pagePlaylist);
  }, [pagePlaylist]);

  const handleRulesClick = useCallback(() => {
    if (!pagePlaylist) return;
    analytics.trackPlaylistRulesOpened({
      playlistId: pagePlaylist.id,
    });
    page.openModal(createItemFromPlaylist(pagePlaylist), [], 'rules');
  }, [pagePlaylist, page, createItemFromPlaylist]);

  const handleTagsClick = useCallback(() => {
    if (!pagePlaylist) return;
    analytics.trackPlaylistTagsOpened({
      playlistId: pagePlaylist.id,
    });
    page.openModal(createItemFromPlaylist(pagePlaylist), [], 'tags');
  }, [pagePlaylist, page, createItemFromPlaylist]);

  const { mutateAsync: copyPlaylist } = useCopyPlaylist();
  const handleDuplicateClick = useCallback(async () => {
    if (!pagePlaylist) return;
    analytics.trackPlaylistDuplicateClicked({
      playlistId: pagePlaylist.id,
    });

    try {
      const newPlaylist = await copyPlaylist({
        playlistId: pagePlaylist.id,
        params: {
          name: getDuplicateResourceName(pagePlaylist.name, []),
        },
      });

      toast.add({
        title: 'Playlist duplicated',
        description: 'Playlist duplicated successfully',
      });

      history.push(paths.editPlaylist(newPlaylist.id, queryParams));
    } catch {
      toast.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  }, [pagePlaylist, copyPlaylist, toast, history, queryParams]);

  const [scheduleModalPlaylist, setScheduleModalPlaylist] = useState<
    typeof pagePlaylist | null
  >(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [affectedDevicesCount, setAffectedDevicesCount] = useState<
    number | null
  >(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const doSave = useCallback(async () => {
    if (rootPlaylistId) {
      flushSync(() => {
        page.updatePlaylist(rootPlaylistId, { name: playlistName });
      });
    }
    const result = await page.submit(queryParams.deviceId);
    if (result && pagePlaylist) {
      const savedRootPlaylist = result[pagePlaylist.id];
      if (savedRootPlaylist && savedRootPlaylist.id !== pagePlaylist.id) {
        history.replace(paths.editPlaylist(savedRootPlaylist.id, queryParams));
      }
    }
  }, [rootPlaylistId, page, playlistName, queryParams, pagePlaylist, history]);

  const handleSaveClick = useCallback(async () => {
    if (uploadingIds.size > 0) {
      setSaveErrorMessage(
        'Some items are still uploading. Please wait until all uploads are complete before saving.',
      );
      return;
    }
    const hasFailedItem = (pagePlaylist?.items ?? []).some((item) => {
      if (!item.presentationId) return false;
      const presentation = page.getPresentation(item.presentationId);
      if (!presentation) return false;
      return getPresentationFileStatus(presentation, localUploads).hasError;
    });
    if (hasFailedItem) {
      setSaveErrorMessage(
        'Some items have failed uploads. Please remove or replace them before saving.',
      );
      return;
    }
    if ((pagePlaylist?.items?.length ?? 0) === 0) {
      setSaveErrorMessage(
        'Your playlist is empty. Please add content before saving.',
      );
      return;
    }
    setIsSaving(true);
    try {
      // A new playlist has a client-side temp id (new-*) and can't be on any
      // device yet, so skip the affected-devices lookup entirely.
      const devices =
        !rootPlaylistId || isNewId(rootPlaylistId)
          ? []
          : await miraClient.getAffectedDevices({
              playlistIds: [rootPlaylistId],
              presentationIds: [],
              folderIds: [],
            });
      setAffectedDevicesCount(devices.length);
      if (devices.length > 0) {
        setIsSaving(false);
        setIsSaveConfirmOpen(true);
        return;
      }
    } catch {
      setAffectedDevicesCount(null);
    }
    await doSave();
    setIsSaving(false);
  }, [rootPlaylistId, doSave, uploadingIds, pagePlaylist, page, localUploads]);

  const handleSaveConfirm = useCallback(async () => {
    setIsSaving(true);
    await doSave();
    setIsSaving(false);
    setIsSaveConfirmOpen(false);
  }, [doSave]);

  const handleDeleteClick = useCallback(() => {
    setConfirmDelete(true);
  }, []);

  const handleDeleteSubmit = useCallback(() => {
    setConfirmDelete(false);
    history.push(queryParams.backTo);
  }, [history, queryParams.backTo]);

  const showRulesAndTags = !!currentUser?.domainId;

  const hasSchedule = !!pagePlaylist?.startDatetime;
  const isNameEmpty = playlistName.trim() === '';
  const hasNameErrorFromState =
    !!rootPlaylistId && !!page.getPlaylistNameError(rootPlaylistId);
  const hasNameError =
    !!rootPlaylistId &&
    (hasNameErrorFromState || (isNameTouched && isNameEmpty));

  // Preview expand/collapse state
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const togglePreviewExpanded = useCallback(() => {
    setIsPreviewExpanded((prev) => !prev);
  }, []);

  return (
    <Page title={pageTitle} backTo={queryParams.backTo}>
      <PlaylistPageContext.Provider value={page}>
        <ScheduleVisibilityContext.Provider
          value={shouldHidePlaylistByInitialLoad}
        >
          <div className="flex flex-col h-full p-6 lg:px-9">
            {/* Header - back button, title, save */}
            <div className="flex justify-between align-top mb-6">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  className="pt-1 pr-2"
                  onClick={handleBack}
                  aria-label="Go back"
                >
                  <ArrowLeft size={26} className="text-black" />
                </button>
                <div>
                  <Text variant="header" as="h1" className="text-black">
                    {title}
                  </Text>
                  <p className="text-xl text-gray-400">
                    Add content and schedule your playlists
                  </p>
                </div>
              </div>
              <PlaylistHeaderV2
                onSave={handleSaveClick}
                isSaveDisabled={!page.isDirty || page.hasError || isNameEmpty}
                isSaving={isSaving}
              />
            </div>

            {/* Playlist name + schedule + more actions */}
            <div
              className="transition-all duration-300 ease-in-out overflow-hidden"
              style={{
                maxHeight: isPreviewExpanded ? 0 : '200px',
                opacity: isPreviewExpanded ? 0 : 1,
              }}
            >
              <PlaylistNameInput
                name={playlistName}
                onNameChange={handleNameChange}
                onNameBlur={handleNameBlur}
                onScheduleClick={handleScheduleClick}
                onRulesClick={showRulesAndTags ? handleRulesClick : undefined}
                onTagsClick={showRulesAndTags ? handleTagsClick : undefined}
                onDuplicateClick={handleDuplicateClick}
                onDeleteClick={!isNewPlaylist ? handleDeleteClick : undefined}
                hasSchedule={hasSchedule}
                hasError={hasNameError}
                isDisabled={!pagePlaylist}
                shouldAutoSelectName={isNewPlaylist}
              />
            </div>

            {/* Content area */}
            {didPageError && (
              <div className="flex-1 flex items-center justify-center">
                <Text variant="body" className="text-gray-400">
                  Oops! Something went wrong.
                </Text>
              </div>
            )}

            {isPageLoading && (
              <div className="flex-1 flex items-center justify-center">
                <Spinner className="size-10" />
              </div>
            )}

            {didPageLoad && (
              <div
                className={`flex flex-col-reverse lg:flex-row flex-1 min-h-0 transition-all duration-300 ease-in-out ${
                  isPreviewExpanded ? 'gap-0' : 'gap-6'
                }`}
              >
                {/* Left column - Playlist builder */}
                <div
                  className="transition-all duration-300 ease-in-out overflow-hidden flex flex-col max-w-[580px]"
                  style={{
                    flex: isPreviewExpanded ? '0 0 0px' : '1 1 0%',
                    opacity: isPreviewExpanded ? 0 : 1,
                    width: isPreviewExpanded ? 0 : undefined,
                  }}
                >
                  <PlaylistItemListV2
                    playlist={pagePlaylist}
                    onAddContent={selectLibraryItems}
                  />
                </div>

                {/* Right column - Preview */}
                <PlaylistPreviewV2
                  presentationId={page.state.previewItem?.presentationId}
                  isExpanded={isPreviewExpanded}
                  onToggleExpand={togglePreviewExpanded}
                  hasItems={(pagePlaylist?.items?.length ?? 0) > 0}
                />
              </div>
            )}
          </div>

          <PlaylistItemModal />

          {scheduleModalPlaylist && (
            <PlaylistScheduleModalV2
              isOpen
              playlist={scheduleModalPlaylist}
              onClose={() => setScheduleModalPlaylist(null)}
            />
          )}

          <DeletePlaylistDialog
            isOpen={confirmDelete}
            playlistId={pagePlaylist?.id}
            playlistName={pagePlaylist?.name}
            onClose={() => setConfirmDelete(false)}
            onSubmit={handleDeleteSubmit}
          />

          <Modal isOpen={isSaveConfirmOpen} className="!max-w-[480px]">
            <Dialog
              showCloseButton={!isSaving}
              onClose={() => {
                if (!isSaving) setIsSaveConfirmOpen(false);
              }}
            >
              <DialogHeader>
                <DialogHeading>
                  <span className="text-black">Confirm Save</span>
                </DialogHeading>
              </DialogHeader>
              <div className="px-4 pt-2 pb-2">
                <Text className="text-gray-700 text-lg">
                  {affectedDevicesCount === null || affectedDevicesCount === 0
                    ? 'This playlist is not currently assigned to any screens.'
                    : `This playlist is currently active on ${affectedDevicesCount} screen${
                        affectedDevicesCount === 1 ? '' : 's'
                      }.`}{' '}
                  Are you sure you want to save your changes?
                </Text>
              </div>
              <div className="flex flex-col-reverse gap-3 pt-4 pb-4 px-4 md:flex-row">
                <Button
                  onPress={() => setIsSaveConfirmOpen(false)}
                  variant="secondary"
                  size="large"
                  className="grow"
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  onPress={handleSaveConfirm}
                  variant="primary"
                  size="large"
                  className="grow"
                  isDisabled={isSaving}
                >
                  {isSaving ? (
                    <span className="flex items-center gap-2">
                      <Spinner size="small" className="text-white" />
                      Saving...
                    </span>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </Dialog>
          </Modal>
          <Modal isOpen={!!saveErrorMessage} className="!max-w-[480px]">
            <Dialog showCloseButton onClose={() => setSaveErrorMessage(null)}>
              <DialogHeader>
                <DialogHeading>
                  <span className="text-black">Unable to Save</span>
                </DialogHeading>
              </DialogHeader>
              <div className="px-4 pt-2 pb-2">
                <Text className="text-gray-700 text-lg">
                  {saveErrorMessage}
                </Text>
              </div>
              <div className="flex p-4 pt-6">
                <Button
                  onPress={() => setSaveErrorMessage(null)}
                  size="large"
                  className="grow"
                >
                  Okay
                </Button>
              </div>
            </Dialog>
          </Modal>
        </ScheduleVisibilityContext.Provider>
      </PlaylistPageContext.Provider>
    </Page>
  );
};

export default PlaylistPageV2;
